import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL || 'https://smapi.sanmartino26.al'}/api`;

const TIME_SLOTS = [
  "12:00", "12:30", "13:00", "13:30", "14:00", "14:30",
  "18:00", "18:30", "19:00", "19:30", "20:00", "20:30",
  "21:00", "21:30", "22:00", "22:30",
];

const PARTY_SIZES = Array.from({ length: 12 }, (_, i) => i + 1);

const initial = {
  full_name: "",
  phone: "",
  email: "",
  party_size: "",
  date: null,
  time: "",
  special_requests: "",
};

export const Reservation = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.email || !form.party_size || !form.date || !form.time) {
      toast.error("Ju lutem plotësoni të gjitha fushat e detyrueshme.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        party_size: Number(form.party_size),
        date: format(form.date, "yyyy-MM-dd"),
        time: form.time,
        special_requests: form.special_requests.trim(),
      };
      await axios.post(`${API}/reservations`, payload);
      setSuccess(true);
      setForm(initial);
      toast.success("Rezervimi u dërgua. Do t'ju kontaktojmë për konfirmim.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Diçka shkoi gabim. Provoni sërish.";
      toast.error(typeof msg === "string" ? msg : "Gabim në server.");
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <section
      id="rezervo"
      data-testid="reservation-section"
      className="relative bg-transparent py-24 md:py-36"
    >
      <div className="sm-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left text */}
          <div className="lg:col-span-5">
            <Reveal>
              <p className="sm-eyebrow">· 04 — Rezervim</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2
                data-testid="reservation-title"
                className="font-serif text-5xl md:text-6xl text-sm-text mt-6 leading-[0.95]"
              >
                Një vend në <span className="italic font-light">tryezën</span> tonë.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 text-base md:text-lg text-sm-muted leading-[1.8] font-light max-w-md">
                Rezervoni darkën tuaj në San Martino 26. Ekipi ynë do t'ju
                kontaktojë për konfirmimin e tavolinës dhe çdo kërkesë të
                veçantë.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-12 space-y-5 max-w-sm">
                <div className="flex items-baseline justify-between border-b border-sm-border pb-3">
                  <span className="text-[0.7rem] tracking-[0.28em] uppercase text-sm-olive">
                    Drekë
                  </span>
                  <span className="font-serif text-sm-text">12:00 — 15:00</span>
                </div>
                <div className="flex items-baseline justify-between border-b border-sm-border pb-3">
                  <span className="text-[0.7rem] tracking-[0.28em] uppercase text-sm-olive">
                    Darkë
                  </span>
                  <span className="font-serif text-sm-text">18:00 — 23:00</span>
                </div>
                <div className="flex items-baseline justify-between border-b border-sm-border pb-3">
                  <span className="text-[0.7rem] tracking-[0.28em] uppercase text-sm-olive">
                    Telefon
                  </span>
                  <a
                    href="tel:+355692000026"
                    className="font-serif text-sm-text sm-link-underline"
                  >
                    +355 69 200 00 26
                  </a>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Right form */}
          <Reveal delay={0.2} className="lg:col-span-7">
            {success ? (
              <div
                data-testid="reservation-success"
                className="border border-sm-border bg-sm-surface p-12 md:p-16"
              >
                <p className="sm-eyebrow text-sm-olive">U dërgua</p>
                <h3 className="font-serif text-3xl md:text-4xl text-sm-text mt-6">
                  Faleminderit. Rezervimi juaj është regjistruar.
                </h3>
                <p className="mt-6 text-sm-muted font-light leading-relaxed">
                  Ekipi ynë do t'ju kontaktojë shumë shpejt për të konfirmuar
                  detajet e tavolinës. Mirë se vini në San Martino 26.
                </p>
                <button
                  data-testid="reservation-new-btn"
                  onClick={() => setSuccess(false)}
                  className="mt-8 inline-block border-b border-sm-text text-[0.75rem] tracking-[0.28em] uppercase font-medium hover:text-sm-olive transition-colors"
                >
                  Bëj një rezervim tjetër
                </button>
              </div>
            ) : (
              <form
                data-testid="reservation-form"
                onSubmit={handleSubmit}
                className="border border-sm-border bg-sm-surface p-8 md:p-12 space-y-7"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Emri dhe Mbiemri *">
                    <input
                      data-testid="reservation-name-input"
                      type="text"
                      required
                      value={form.full_name}
                      onChange={(e) => update("full_name", e.target.value)}
                      className="sm-input"
                      placeholder="Ardit Curri"
                    />
                  </Field>
                  <Field label="Numri i Personave *">
                    <Select
                      value={form.party_size}
                      onValueChange={(v) => update("party_size", v)}
                    >
                      <SelectTrigger
                        data-testid="reservation-party-trigger"
                        className="sm-input justify-between"
                      >
                        <SelectValue placeholder="Zgjidh" />
                      </SelectTrigger>
                      <SelectContent data-testid="reservation-party-content">
                        {PARTY_SIZES.map((n) => (
                          <SelectItem
                            key={n}
                            value={String(n)}
                            data-testid={`reservation-party-${n}`}
                          >
                            {n} {n === 1 ? "person" : "persona"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Telefon *">
                    <input
                      data-testid="reservation-phone-input"
                      type="tel"
                      required
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      className="sm-input"
                      placeholder="+355 69 ..."
                    />
                  </Field>
                  <Field label="Email *">
                    <input
                      data-testid="reservation-email-input"
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      className="sm-input"
                      placeholder="emri@example.com"
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Data *">
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          data-testid="reservation-date-trigger"
                          type="button"
                          className="sm-input flex items-center justify-between text-left"
                        >
                          <span className={form.date ? "text-sm-text" : "text-sm-muted/70"}>
                            {form.date ? format(form.date, "dd MMM yyyy") : "Zgjidh datën"}
                          </span>
                          <CalendarIcon className="w-4 h-4 text-sm-olive" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        data-testid="reservation-date-popover"
                        className="p-0 bg-sm-bg border-sm-border"
                        align="start"
                      >
                        <Calendar
                          mode="single"
                          selected={form.date}
                          onSelect={(d) => update("date", d)}
                          disabled={(d) => d < today}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </Field>

                  <Field label="Ora * (12:00–15:00 / 18:00–23:00)">
                    <Select
                      value={form.time}
                      onValueChange={(v) => update("time", v)}
                    >
                      <SelectTrigger
                        data-testid="reservation-time-trigger"
                        className="sm-input justify-between"
                      >
                        <SelectValue placeholder="Zgjidh orën" />
                      </SelectTrigger>
                      <SelectContent
                        data-testid="reservation-time-content"
                        className="max-h-72"
                      >
                        <div className="px-3 py-2 text-[0.65rem] tracking-[0.28em] uppercase text-sm-olive">
                          Drekë
                        </div>
                        {TIME_SLOTS.filter((t) => t < "15:00").map((t) => (
                          <SelectItem
                            key={t}
                            value={t}
                            data-testid={`reservation-time-${t}`}
                          >
                            {t}
                          </SelectItem>
                        ))}
                        <div className="px-3 py-2 text-[0.65rem] tracking-[0.28em] uppercase text-sm-olive border-t border-sm-border mt-1">
                          Darkë
                        </div>
                        {TIME_SLOTS.filter((t) => t >= "18:00").map((t) => (
                          <SelectItem
                            key={t}
                            value={t}
                            data-testid={`reservation-time-${t}`}
                          >
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <Field label="Kërkesa të Veçanta">
                  <textarea
                    data-testid="reservation-notes-input"
                    rows={3}
                    value={form.special_requests}
                    onChange={(e) => update("special_requests", e.target.value)}
                    className="sm-input resize-none"
                    placeholder="Alergji, festë e veçantë, preferenca tavoline..."
                  />
                </Field>

                <button
                  data-testid="reservation-submit-btn"
                  type="submit"
                  disabled={submitting}
                  className="w-full md:w-auto inline-flex items-center justify-center gap-3 bg-sm-text text-sm-bg px-10 py-4 text-[0.75rem] tracking-[0.28em] uppercase font-medium hover:bg-sm-olive transition-colors duration-500 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? "Duke dërguar..." : "Dërgo Rezervimin"}
                  {!submitting && <span>→</span>}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>

      <style>{`
        .sm-input {
          width: 100%;
          background-color: hsl(var(--sm-bg));
          border: 1px solid hsl(var(--sm-border));
          padding: 14px 16px;
          font-family: "Inter", sans-serif;
          font-size: 14px;
          color: hsl(var(--sm-text));
          outline: none;
          transition: border-color 0.3s ease, box-shadow 0.3s ease;
          border-radius: 2px;
        }
        .sm-input::placeholder { color: hsl(var(--sm-text-muted) / 0.6); }
        .sm-input:focus { border-color: hsl(var(--sm-olive)); box-shadow: 0 0 0 2px hsl(var(--sm-olive) / 0.12); }
      `}</style>
    </section>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[0.65rem] tracking-[0.28em] uppercase text-sm-olive mb-3 font-medium">
      {label}
    </span>
    {children}
  </label>
);

export default Reservation;
